import type { FavouriteNotificationPreferenceListResponseType } from "@sellar-io/api-v2/types";
import { createSellarColumnHelper } from "@sellar-io/components";

import { useMemo } from "react";
import { ProfilePicture } from "../../../components";
import { TableInfinite } from "../../../components/table/TableInfinite";
import { useFavouriteNotificationPreferenceList } from "../../../hooks/api/v2/favouriteNotificationPreference/useFavouriteNotificationPreferenceList";
import { useFavouriteNotificationPreferenceUpdate } from "../../../hooks/api/v2/favouriteNotificationPreference/useFavouriteNotificationPreferenceUpdate";
import { PreferenceCheckbox } from "./NotificationSettings";

export const FavouriteNotificationSettingsTable = ({
  pushNotificationsDisabled,
}: {
  pushNotificationsDisabled: boolean;
}) => {
  const favouriteNotificationPreferenceListQuery =
    useFavouriteNotificationPreferenceList({
      limit: 10,
    });

  const { data, fetchNextPage, isFetching } =
    favouriteNotificationPreferenceListQuery;

  const flatData = useMemo(
    () => data?.pages.flatMap((page) => page) ?? [],
    [data],
  );

  const columnHelper =
    createSellarColumnHelper<
      FavouriteNotificationPreferenceListResponseType[number]
    >();

  const columns = [
    columnHelper.accessor((row) => row, {
      id: "connection",
      header: "Connection",
      enableSorting: false,
      cell: (info) => {
        const preference = info.getValue();
        const supplierBusiness =
          preference.RetailerRelationshipFavourite.RetailerRelationship
            .Relationship.Supplier.Business;

        if (!supplierBusiness) {
          return <span>Unknown supplier</span>;
        }

        return (
          <div className="flex items-center">
            <figure className="relative">
              <ProfilePicture
                size="small"
                name={supplierBusiness.name}
                logoUrl={supplierBusiness.logoUrl}
                className="mr-2 h-[28px] max-h-[28px] min-h-[28px] w-[28px] max-w-[28px] min-w-[28px] text-[8px]"
              />
            </figure>
            <span>{supplierBusiness.name}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "email",
      header: "Email",
      size: 80,
      enableSorting: false,
      cell: (info) => {
        const preference = info.getValue();
        return (
          <NotificationCheckbox
            preference={preference}
            isEnabled={preference.emailProductUpdates}
            notificationType={"emailProductUpdates"}
          />
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "push",
      header: "Push",
      size: 80,
      enableSorting: false,
      cell: (info) => {
        const preference = info.getValue();
        return (
          <NotificationCheckbox
            preference={preference}
            isEnabled={preference.pushProductUpdates}
            disabled={pushNotificationsDisabled}
            notificationType={"pushProductUpdates"}
          />
        );
      },
    }),
  ];

  return (
    <TableInfinite
      columns={columns}
      data={flatData}
      roundedTop={true}
      infiniteQuery={{
        data: flatData,
        fetchNextPage,
        isFetching,
      }}
      noResultsText="No notification preferences found"
      gridClasses={"grid-cols-[1fr_80px_80px]"}
    />
  );
};

const NotificationCheckbox = ({
  preference,
  isEnabled,
  disabled,
  notificationType,
}: {
  preference: FavouriteNotificationPreferenceListResponseType[number];
  isEnabled: boolean;
  disabled?: boolean;
  notificationType: "emailProductUpdates" | "pushProductUpdates";
}) => {
  const updatePreferenceMutation = useFavouriteNotificationPreferenceUpdate(
    preference.id,
  );

  const handleToggle = async () => {
    updatePreferenceMutation.mutate({
      [notificationType]: !isEnabled,
    });
  };

  return (
    <PreferenceCheckbox
      checked={isEnabled}
      disabled={disabled}
      onChange={() => {
        handleToggle();
      }}
      tooltip="Enable push notifications on this device to edit preference"
    />
  );
};
